<template>
  <v-app>
    <v-app-bar id="sidebarToolbar" color="bg-white" dark :flat="true" class="pa-0">
      <template v-slot:prepend>
        <v-toolbar-title class="px-0 white text-primary">
          <v-img
            :src="mini ? imgsPath + miniLogo : imgsPath + logo"
            class="mx-0"
            position="center left"
            max-height="54"
            width="auto"
            max-width="246px"
          />
        </v-toolbar-title>
      </template>
    </v-app-bar>
    <v-navigation-drawer
      v-model="drawer"
      id="sidebar"
      :permanent="true"
      class="bg-primary text-white"
      height="calc(100% - 40px)"
      dark
      :rail="mini"
    >
      <v-btn
        class="mx-2 my-4"
        :icon="mini ? 'mdi-menu' : 'mdi-backburger'"
        id="openSidebar"
        min-width="40"
        min-height="40"
        rounded="lg"
        size="small"
        color="white"
        flat
        @click.stop="setDrawerSidebar"
      >
        <v-icon size="large"></v-icon>
        <v-tooltip activator="parent" location="start">
          {{ mini ? $t('labels.open-sidebar') : $t('labels.close-sidebar') }}
        </v-tooltip>
      </v-btn>
      <v-list :nav="true" density="compact">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          :link="true"
          :active="isActiveRoute(item.link)"
          @click="linkRoute(item.link)"
        >
          <template v-slot:prepend>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <v-icon class="text-white" v-bind="props">
                  {{ item.icon }}
                </v-icon>
              </template>
              {{ $t(item.title) }}
            </v-tooltip>
          </template>

          <v-list-item-title class="text-white text-uppercase">
            {{ $t(item.title) }}
          </v-list-item-title>
        </v-list-item>
      </v-list>

      <div v-if="favorites?.enabled">
        <v-divider></v-divider>
        <p class="ma-3 text-white opacity" v-if="favoriteItems.length === 0 && !mini">
          <small>{{ $t('strings.favorite-text-first') }}</small>
          <v-spacer class="ma-3"></v-spacer>
          <small>{{ $t('strings.favorite-text-second') }}</small>
        </p>
      </div>

      <v-list :nav="true" density="compact" v-if="favorites?.enabled">
        <v-list-item :key="item.sid || item.title" v-for="(item, index) in favoriteItems">
          <template v-slot:prepend>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <v-icon class="text-white" v-bind="props">
                  {{ item.icon }}
                </v-icon>
              </template>
              {{ $t(item.title) }}
            </v-tooltip>
          </template>

          <v-list-item-title
            :target="item.link"
            :to="item.link"
            :href="item.link"
            class="text-white text-uppercase no-decoration-link"
          >
            <router-link class="text-white text-decoration-none" :to="item.link" target="_blank">
              {{ $t(item.title) }}
            </router-link>
          </v-list-item-title>
          <v-tooltip v-if="!mini" location="top end">
            <template v-slot:activator="{ props }">
              <v-icon
                size="x-small"
                class="remove-favorite"
                @click="removeFavorite(item, index)"
                v-bind="props"
              >
                mdi-close
              </v-icon>
            </template>
            <span>{{ $tc('labels.remove-favorite-tooltip') }}</span>
          </v-tooltip>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <MenuItemTools
          v-if="showServicesMenu"
          :jsonTools="mfConfig.jsonTools"
          :tenant="realm"
        ></MenuItemTools>
        <MenuUser :realm="realm" :mfConfig="mfConfig"></MenuUser>
      </template>
    </v-navigation-drawer>
    <v-overlay z-index="10" v-if="overlay" text>
      <div class="ma-5 text-center">
        <div class="ma-5">
          {{ $t('strings.overlay-text') }}
        </div>
        <v-btn color="info" @click="overlay = false"> OK </v-btn>
      </div>
    </v-overlay>
  </v-app>
</template>

<script>
import utils from '@/mixins/utils.js'
import axios from 'axios'
import MenuUser from '@/components/MenuUser.vue'
import MenuItemTools from '@/components/MenuItemTools.vue'

export default {
  name: 'NavigationDrawer',
  mixins: [utils],
  components: {
    MenuUser,
    MenuItemTools,
  },
  props: ['realm', 'mfConfig'],
  data() {
    return {
      favoriteItems: [],
      mini: true,
      drawer: true,
      loggedin: false,
      overlay: false,
      windowWidth: window.outerWidth,
      orientationType: window.screen.orientation.type,
      resizeHandler: null,
      favoriteItemsChangedHandler: null,
      currentPath: window.location.pathname,
    }
  },
  mounted() {
    if (this.windowWidth <= 1024) {
      localStorage.setItem('sidenav', 'closed')
      this.emitSidenavChange()
    }
    this.mini = localStorage.getItem('sidenav') !== 'opened'

    this.resizeHandler = () => {
      this.windowWidth = window.outerWidth
    }
    window.addEventListener('resize', this.resizeHandler)

    this.overlay = this.$vuetify.display.thresholds.xs
    this.getFavoriteServices()

    this.favoriteItemsChangedHandler = () => {
      this.getFavoriteServices()
    }
    document.addEventListener('favorite-items-changed', this.favoriteItemsChangedHandler)

    // Monitora i cambiamenti di route
    if (this.$router) {
      this.$router.afterEach((to) => {
        this.currentPath = to.path
      })
    }

    // Monitora i cambiamenti di URL per link esterni (single-spa)
    window.addEventListener('single-spa:routing-event', this.updateCurrentPath)
    window.addEventListener('popstate', this.updateCurrentPath)
  },
  beforeUnmount() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.favoriteItemsChangedHandler) {
      document.removeEventListener('favorite-items-changed', this.favoriteItemsChangedHandler)
    }
    window.removeEventListener('single-spa:routing-event', this.updateCurrentPath)
    window.removeEventListener('popstate', this.updateCurrentPath)
  },
  watch: {
    mini(val) {
      localStorage.setItem('sidenav', val ? 'closed' : 'opened')
      this.emitSidenavChange()
    },
  },
  computed: {
    imgsPath() {
      return this.mfConfig ? this.mfConfig.storageImgs : null
    },
    items() {
      return this.mfConfig ? this.mfConfig.links : null
    },
    apiUrl() {
      return this.mfConfig ? this.mfConfig.api : null
    },
    logo() {
      return this.mfConfig ? this.mfConfig.logo : null
    },
    miniLogo() {
      return this.mfConfig ? this.mfConfig.miniLogo : null
    },
    favorites() {
      return this.mfConfig ? this.mfConfig.favorites : null
    },
    showServicesMenu() {
      return this.mfConfig ? this.mfConfig.showServicesMenu : null
    },
  },
  methods: {
    setDrawerSidebar: function () {
      this.mini = !this.mini
    },
    emitSidenavChange() {
      document.dispatchEvent(
        new CustomEvent('sidenav-localstorage-changed', {
          detail: {
            storage: localStorage.getItem('sidenav'),
          },
        }),
      )
    },
    removeFavorite(item, index) {
      axios
        .delete(this.apiUrl + '/favorites/' + item.sid, {
          withCredentials: true,
        })
        .then(() => {
          this.favoriteItems.splice(index, 1)
          document.dispatchEvent(
            new CustomEvent('favorite-item-removed', {
              detail: {
                favoriteItems: this.favoriteItems,
                favoriteItem: item,
              },
            }),
          )
        })
        .catch((error) => {
          console.error('Error removing favorite:', error)
          // Opzionale: mostrare un messaggio di errore all'utente
        })
    },
    getFavoriteServices() {
      if (this.favorites?.enabled) {
        axios
          .get(this.apiUrl + '/favorites', { withCredentials: true })
          .then((response) => {
            this.favoriteItems = response.data
          })
          .catch((error) => {
            console.error('Error fetching favorites:', error)
            // Opzionale: mostrare un messaggio di errore all'utente
          })
      }
    },
    linkRoute(link) {
      if (link.includes('http')) {
        window.open(link, '_blank')
      } else {
        this.$router.push(link)
        this.currentPath = link
      }
    },
    isActiveRoute(link) {
      // Se il link è esterno (http/https), non può essere attivo
      if (link.includes('http')) {
        return false
      }

      // Confronto esatto
      if (this.currentPath === link) {
        return true
      }

      // Confronto per path che inizia con il link (per sottopagine)
      // Esclude il caso in cui link è '/' per evitare che sia sempre attivo
      if (link !== '/' && this.currentPath.startsWith(link)) {
        return true
      }

      return false
    },
    updateCurrentPath() {
      this.currentPath = window.location.pathname
    },
  },
}
</script>

<style scoped>
nav#sidebar {
  height: calc(100% - 64px - 40px) !important;
}

#sidebar #sidebarToolbar :deep(.v-toolbar__content),
#sidebar #sidebarToolbar :deep(.v-toolbar__extension) {
  padding: 0 !important;
}

#sidebarToolbar {
  z-index: 1004 !important;
}

.divider-left {
  width: 6%;
}

.favorite-content {
  position: relative !important;
}
.no-decoration-link {
  text-decoration: none;
}

.favorite-title {
  white-space: normal;
}

.remove-favorite {
  position: absolute !important;
  right: 0;
  cursor: pointer;
  top: 25%;
}

.divider-middle {
  width: 9%;
}

.mini-divider-left {
  width: 27.5%;
}

.mini-divider-right {
  width: 27.5%;
}

.opacity {
  opacity: 0.6;
}

.v-icon {
  opacity: 1 !important;
}

.divider-right {
  width: 44.2%;
}

.icon-middle {
  vertical-align: middle;
}

.favorite-text {
  vertical-align: bottom;
  margin-left: 10px;
  margin-right: 5px;
  font-size: 0.8125rem;
  text-transform: uppercase;
}
</style>
